import { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { EDeviceTypes } from "../../../../shared/oversight-core/enums/device-types";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import findIcon from "../../../../shared/oversight-core/utils/findIcon";

export enum ESimplifiedEnergyUsageType {
  AMOUNT = "AMOUNT",
  WATTAGE = "WATTAGE",
  USAGE = "USAGE",
}

export interface ISimplifiedDevice {
  deviceType: EDeviceTypes;
  deviceName: string;
  amount: string;
  wattage: string;
  usage: string;
  id: string;
  units: number;
  maxPower: number;
}

interface IProps {
  device: ISimplifiedDevice;
  bgColor?: string;
  onChange: (
    type: ESimplifiedEnergyUsageType,
    id: string,
    value: string
  ) => void;
  onUnitChange: (id: string, units: number) => void;
  onMaxPowerChange: (id: string, maxPower: number) => void;
  showErrorMessage: boolean;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

const SimplifiedDeviceEnergyCard = (props: IProps) => {
  const {
    device,
    bgColor,
    showErrorMessage,
    onChange,
    onUnitChange,
    onMaxPowerChange,
    onDelete,
    disabled,
  } = props;

  useEffect(() => {
    const amount = Number(device.amount);
    const wattage = Number(device.wattage);
    const usage = Number(device.usage);

    const units = amount * (wattage / 1000) * usage;
    onUnitChange(device.id, units);
  }, [device.amount, device.wattage, device.usage]);

  useEffect(() => {
    const amount = Number(device.amount);
    const wattage = Number(device.wattage);

    const maxPower = amount * wattage;
    onMaxPowerChange(device.id, maxPower);
  }, [device.amount, device.wattage]);

  return (
    <Row className={`align-items-center px-4 py-3 mx-0 ${bgColor}`}>
      <Col xl={3}>
        <Row className="align-items-center">
          <Col className={`${device.deviceType} col-auto p-2 rounded`}>
            <MaterialIcon
              icon={
                device.deviceType === EDeviceTypes.GENERAL
                  ? "dashboard"
                  : findIcon(device.deviceType as EDeviceTypes)
              }
            />
          </Col>
          <Col className="title-2 font-weight-500 font-size-12 ps-2">
            {device.deviceName}
          </Col>
        </Row>
      </Col>
      <Col className="pe-0">
        <Row className="align-items-center row-cols-4 justify-content-end">
          <Col xl={3}>
            <AppInput
              label="Quantity"
              placeholder="Quantity"
              type="number"
              name="amount"
              value={device.amount}
              errors={
                showErrorMessage &&
                (device.amount.trim() === "" || Number(device.amount) <= 0)
                  ? {
                      amount: {
                        message: "Amount is required",
                      },
                    }
                  : Error
              }
              isRequired={false}
              labelFontSize="font-size-12"
              isShowInfoIcon
              onChange={(event) => {
                const value = event.target.value;

                if (value === "" || Number(value) > 0) {
                  onChange(ESimplifiedEnergyUsageType.AMOUNT, device.id, value);
                }
              }}
              disabled={disabled}
            />
          </Col>
          <Col xl={3}>
            <AppInput
              label="Wattage"
              placeholder="Wattage"
              type="number"
              name="wattage"
              value={device.wattage}
              errors={
                showErrorMessage &&
                (device.wattage.trim() === "" || Number(device.wattage) <= 0)
                  ? {
                      wattage: {
                        message: "Wattage is required",
                      },
                    }
                  : Error
              }
              isRequired={false}
              labelFontSize="font-size-12"
              isShowInfoIcon
              onChange={(event) => {
                const value = event.target.value;

                if (value === "" || Number(value) > 0) {
                  onChange(
                    ESimplifiedEnergyUsageType.WATTAGE,
                    device.id,
                    value
                  );
                }
              }}
              disabled={disabled}
            />
          </Col>
          <Col xl={3}>
            <AppInput
              label="Daily Usage (Hours)"
              placeholder="Daily Usage"
              type="number"
              name="usage"
              value={device.usage}
              errors={
                showErrorMessage &&
                (device.usage.trim() === "" ||
                  Number(device.usage) <= 0)
                  ? {
                      usage: {
                        message: "Daily usage is required",
                      },
                    }
                  : Error
              }
              isRequired={false}
              labelFontSize="font-size-12"
              isShowInfoIcon
              onChange={(event) => {
                const value = event.target.value;

                if (value === "" || Number(value) > 0) {
                  onChange(
                    ESimplifiedEnergyUsageType.USAGE,
                    device.id,
                    value
                  );
                }
              }}
              disabled={disabled}
            />
          </Col>
          <Col className="text-label font-size-12 font-weight-600" xl={2}>
            {device.units > 0 ? `${device.units.toFixed(2)} Units` : ""}
          </Col>
          {!disabled && (
            <Col className="col-auto">
              <MaterialIcon
                icon="delete"
                color="#E63E35"
                className="cursor-pointer"
                onClick={() => {
                  onDelete(device.id);
                }}
              />
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  );
};

export default SimplifiedDeviceEnergyCard;
